import React, { SyntheticEvent, useEffect, useState } from 'react';
import { FormControl } from 'react-bootstrap';

import { EventType } from '../../interfaces';
import { Alert } from '../atoms';
import { useEventTypes } from '../../hooks';

interface Props {
  selectedEventTypes: EventType[];
  setSelectedEventTypes: (items: EventType[]) => void;
  filterInForm?: boolean;
  required?: boolean;
  allowAll?: boolean;
}

interface BodyProps {
  handleInputChange: (evt: any) => void;
  value: string;
  filtered: EventType[];
  required?: boolean;
  allowAll?: boolean;
  onKeyDown: (evt: React.KeyboardEvent) => void; // Added handler for keydown events
}

const EventTypesFilterBrowserBody = (props: BodyProps) => {
  const { handleInputChange, value, filtered, required, allowAll, onKeyDown } = props;
  return (
    <>
      <FormControl
        className="hdn-input"
        value={value}
        list="eventTypes"
        data-test="event-type-filter-input"
        onInput={handleInputChange}
        onKeyDown={onKeyDown} // Added keydown handler
        disabled={filtered.length === 0}
        type="text"
        required={required}
        placeholder={filtered.length === 0 ? 'No more event types available' : 'Select or start typing'}
      />

      <datalist id="eventTypes">
        {allowAll && <option value="*">All event types</option>}
        {filtered.map((et) => (
          <option key={et.id}>{et.name}</option>
        ))}
      </datalist>
    </>
  );
};

export function EventTypesFilterBrowser(props: Props) {
  const { filterInForm, required, allowAll } = props;
  const eventTypes = useEventTypes();

  const [filtered, setFiltered] = useState<EventType[]>([]);
  const [value, setValue] = useState<string>('');


  const refreshFilteredList = () => {
    const filteredToState = (eventTypes || [])?.filter(
      (et: EventType) => !props.selectedEventTypes?.map((selected) => selected.id).includes(et.id),
    );
    setFiltered(filteredToState);
  };

  useEffect(() => {
    refreshFilteredList();
  }, [props.selectedEventTypes.length, eventTypes?.length]);

  const addEventType = (eventType: EventType): boolean => {
    if (!eventType.id) {
      return false;
    }

    const eventsTypeToAdd = [...props.selectedEventTypes, eventType];

    props.setSelectedEventTypes(eventsTypeToAdd);

    return true;
  };

  const submit = (evt: SyntheticEvent): boolean => {
    const eventToAdd = eventTypes?.find((et) => value.includes(et.name));

    if (eventToAdd && addEventType(eventToAdd)) {
      setValue('');
    } else if (addEventTypeByName(value ?? '')) {
      setValue('');
    } else {
      new Alert('No event type matching this id!').alert();
    }

    evt.preventDefault();
    evt.stopPropagation();
    return false;
  };

  const addEventTypeByName = (name: string): boolean => {
    const singleEventType = eventTypes?.find((et) => et.name.toUpperCase() === name.toUpperCase());
    if (!singleEventType?.id) {
      return false;
    }

    addEventType(singleEventType);

    return true;
  };

  const handleInputChange = (evt: any) => {
    const newValue = evt.target.value;

    if (newValue === '*') {
      props.setSelectedEventTypes(eventTypes)
      setValue('');
      return;
    }

    if (newValue.length >= value.length + 2) {
      // if we're seeing more 2 or more chars at once, it means that items was picked from the list
      if (addEventTypeByName(newValue)) {
        setValue('');
      } else {
        setValue(newValue);
      }
    } else {
      setValue(newValue);
    }
  };

  // Handler for keydown events
  const handleKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.preventDefault(); // Prevent form submission
      submit(evt); // Use existing submit function
    }
  };

  if (filterInForm) {
    return (
      <div>
        <form onSubmit={submit}>
          <EventTypesFilterBrowserBody
            handleInputChange={handleInputChange}
            value={value}
            filtered={filtered}
            allowAll={allowAll}
            onKeyDown={handleKeyDown}
          />
        </form>
      </div>
    );
  }

  return (
    <div>
      <EventTypesFilterBrowserBody
        handleInputChange={handleInputChange}
        value={value}
        filtered={filtered}
        required={required}
        allowAll={allowAll}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
